/**
 * Matching engine centralisé pour la recherche sahha.ma.
 *
 * Stratégie : token-AND scoring. Une query est considérée matcher un haystack
 * si TOUS ses tokens (≥2 chars) trouvent une "ancre" dans le haystack.
 * L'ancre peut être :
 *   - exact match d'un token   → score 100
 *   - prefix match d'un token  → score 85
 *   - substring d'un token     → score 60 (uniquement pour tokens ≥4 chars)
 *   - fuzzy 1-edit             → score 50 (uniquement pour tokens ≥5 chars)
 *
 * Le score global = moyenne pondérée des scores tokens × couverture.
 *
 * Pourquoi token-AND plutôt que strict-substring sur concaténation :
 *
 *   query  = "lakhdar nawal"
 *   doc    = firstName "Nawal" + lastName "Lakhdar"
 *   concat = "nawal lakhdar"
 *
 *   Avec substring : "nawal lakhdar".includes("lakhdar nawal") === false ❌
 *   Avec token-AND : ["lakhdar", "nawal"] tous présents dans tokens(doc) ✅
 *
 * Cas de zero-results corrigés grâce à cette logique :
 *   - "dr lakhdar nawal" → Nawal Lakhdar (permutation prénom/nom)
 *   - "dr hajar elboukhari" → Hajar Elboukhari (préfixe "dr" stripped en amont)
 *   - "chimiotherapie intra peritoneale" → matche "chimiotherapie" dans actes
 */

import { tokenize, normalizeQuery } from "./normalize";

// ─── Levenshtein bounded ─────────────────────────────────────────────────
// Distance d'édition limitée à `max`. Early-exit dès qu'on dépasse → O(n*max).
// Évite l'explosion combinatoire sur les longs noms (early return si |Δlen| > max).
export function bounded_levenshtein(a: string, b: string, max: number): number {
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > max) return max + 1;
  if (la === 0) return lb;
  if (lb === 0) return la;
  const prev = new Array(lb + 1);
  const curr = new Array(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;
  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    let rowMin = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    for (let j = 0; j <= lb; j++) prev[j] = curr[j];
  }
  return prev[lb];
}

/**
 * Score un token unique contre une liste de tokens haystack.
 * Retourne le meilleur score (0-100) trouvé sur l'un des tokens.
 */
function scoreToken(needle: string, hayTokens: string[]): number {
  let best = 0;
  for (const ht of hayTokens) {
    if (ht === needle) return 100; // exact = early return
    if (ht.startsWith(needle)) {
      best = Math.max(best, 85);
      continue;
    }
    if (needle.length >= 4 && ht.includes(needle)) {
      best = Math.max(best, 60);
      continue;
    }
    // Fuzzy 1-edit pour tokens ≥5 chars (couvre elbokhari/elboukhari, valphi/valfi)
    if (needle.length >= 5 && ht.length >= 4) {
      const maxDist = needle.length >= 8 ? 2 : 1;
      const d = bounded_levenshtein(needle, ht, maxDist);
      if (d <= maxDist) {
        best = Math.max(best, 55 - (d - 1) * 10);
      }
    }
  }
  return best;
}

/**
 * Score token-AND : exige que TOUS les tokens du needle aient un match.
 *
 * Retourne 0 si un seul token ne matche aucun tokenable du haystack.
 * Sinon retourne la moyenne des scores tokens (0-100).
 *
 * `scoreTokenAnd("lakhdar nawal", "nawal lakhdar casablanca")` → ~92
 * `scoreTokenAnd("foo bar", "nawal lakhdar")` → 0 (ni foo ni bar matchent)
 */
export function scoreTokenAnd(needleTokens: string[], haystack: string): number {
  if (needleTokens.length === 0) return 0;
  if (!haystack) return 0;
  const hayTokens = haystack.split(" ").filter(Boolean);
  if (hayTokens.length === 0) return 0;

  let total = 0;
  for (const nt of needleTokens) {
    const s = scoreToken(nt, hayTokens);
    if (s === 0) return 0; // un seul token raté → échec global (AND)
    total += s;
  }
  return Math.round(total / needleTokens.length);
}

/**
 * Score "permissif" : utilise OR au lieu de AND mais pondère par couverture.
 *
 * Utile pour les corpus très courts (specialty.nameFr = "Ophtalmologue") où
 * une query longue ne peut pas matcher token-AND. Ici on accepte les matches
 * partiels mais on pénalise la couverture incomplète.
 *
 * `scoreTokenOr(["ophtalmologue", "agadir"], "ophtalmologue")` → 50 (1/2 cov × 100)
 */
export function scoreTokenOr(needleTokens: string[], haystack: string): number {
  if (needleTokens.length === 0) return 0;
  const hayTokens = haystack.split(" ").filter(Boolean);
  if (hayTokens.length === 0) return 0;

  let total = 0;
  let matched = 0;
  for (const nt of needleTokens) {
    const s = scoreToken(nt, hayTokens);
    if (s > 0) {
      matched++;
      total += s;
    }
  }
  if (matched === 0) return 0;
  const coverage = matched / needleTokens.length;
  return Math.round((total / needleTokens.length) * coverage);
}

/**
 * Score le meilleur match parmi plusieurs haystacks (haystacks normalisés).
 * Utile pour scorer un médecin contre [firstName+lastName, firstNameAr+lastNameAr, ...]
 */
export function scoreBestHaystack(
  needleTokens: string[],
  haystacks: string[],
): number {
  let best = 0;
  for (const h of haystacks) {
    const s = scoreTokenAnd(needleTokens, h);
    if (s > best) best = s;
    if (best === 100) return 100;
  }
  return best;
}

/**
 * Wrapper haut-niveau : prend une query brute + un objet "candidat" avec ses
 * champs multilingues, retourne le meilleur score.
 *
 * Utilisé pour scorer Specialties / Cities (qui ont nameFr/nameAr/nameEn/slug).
 */
export function scoreMultilingualEntity(
  needleTokens: string[],
  entity: {
    nameFr?: string | null;
    nameAr?: string | null;
    nameEn?: string | null;
    slug?: string | null;
    aliases?: readonly string[];
  },
): number {
  const haystacks: string[] = [];
  for (const v of [entity.nameFr, entity.nameAr, entity.nameEn, entity.slug]) {
    if (v) haystacks.push(normalizeQuery(v));
  }
  if (entity.aliases) {
    for (const a of entity.aliases) {
      if (a) haystacks.push(normalizeQuery(a));
    }
  }
  // Cherche le meilleur score AND, fallback OR si rien
  const andScore = scoreBestHaystack(needleTokens, haystacks);
  if (andScore > 0) return andScore;
  // Fallback OR : pour les queries longues type "ophtalmologue agadir"
  // qui matchent partiellement un seul champ
  let bestOr = 0;
  for (const h of haystacks) {
    const s = scoreTokenOr(needleTokens, h);
    if (s > bestOr) bestOr = s;
  }
  return bestOr;
}

// Re-export pour ergonomie : la plupart des consommateurs n'ont besoin que
// de prepareQuery + scoreTokenAnd / scoreMultilingualEntity.
export { tokenize };
